import { useState } from "react";
import { toast } from "react-toastify";
import { CODE } from "../../utils/Constant";

interface SelectEditorManagerProps {
    name: string;
    idFieldName?: string;
    values: any;
    updateFields: (id: string, payload: Record<string, any>) => Promise<any>; // Service API
    onUpdateSuccess?: (updatedData: any) => void;
    loadSelectEditorInfo?: (id: string) => Promise<any>; // ⚡ Lazy load selectEditor info
    buildPayload?: (option: any) => Record<string, any>; // Hàm build payload để gửi lên API
    enableConfirmWithReason?: boolean; // Bật/tắt confirm với lý do (default: false)
}

/**
 * Custom Hook quản lý logic edit/update selectEditor
 * Dùng chung cho cả khám bệnh và điều trị
 */
export const useSelectEditorManager = ({
    name,
    idFieldName = "id",
    values,
    updateFields,
    onUpdateSuccess,
    loadSelectEditorInfo,
    buildPayload,
    enableConfirmWithReason = false,
}: SelectEditorManagerProps) => {
    const [isEditingSelect, setIsEditingSelect] = useState(false);
    const [editedValue, setEditedValue] = useState<any>(null);
    const [showValidationConfirm, setShowValidationConfirm] = useState(false);
    const [showConfirmWithReason, setShowConfirmWithReason] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [isLoadingSelectEditorInfo, setIsLoadingSelectEditorInfo] = useState(false);

    const handleEditSelect = async () => {
        setIsEditingSelect(true);

        // ⚡ Lazy load: Nếu chưa có value và có loadSelectEditorInfo, load ngay
        if (!values?.[name] && loadSelectEditorInfo && values?.[idFieldName]) {
            setIsLoadingSelectEditorInfo(true);
            try {
                const selectEditorInfo = await loadSelectEditorInfo(values[idFieldName]);
                if (selectEditorInfo?.[name]) {
                    setEditedValue(selectEditorInfo[name]);
                } else {
                    setEditedValue(null);
                }
            } catch (error) {
                console.error("Error loading selectEditor info:", error);
                setEditedValue(null);
            } finally {
                setIsLoadingSelectEditorInfo(false);
            }
        } else {
            setEditedValue(values?.[name] || null);
        }
    };

    const handleCancelEdit = () => {
        setIsEditingSelect(false);
        setEditedValue(null);
    };

    const handleSaveSelect = async (submitData?: any, allowDisableRules?: boolean) => {
        try {
            // Build payload từ editedValue
            let payload: Record<string, any> = {};

            if (buildPayload && editedValue) {
                // Sử dụng buildPayload để tạo payload tùy chỉnh
                payload = buildPayload(editedValue);
            } else {
                // Fallback: gửi trực tiếp editedValue
                payload = { [name]: editedValue };
            }

            // Merge với submitData (từ reason dialog) nếu có
            if (submitData) {
                payload = { ...payload, ...submitData };
            }

            if (allowDisableRules) {
                payload.allowDisableRules = true;
            }

            const { data } = await updateFields(values?.[idFieldName], payload);

            if (data?.code === CODE.SUCCESS) {
                toast.success("Cập nhật thành công");
                setIsEditingSelect(false);
                setShowValidationConfirm(false);
                setShowConfirmWithReason(false);

                // Callback để parent component cập nhật state
                onUpdateSuccess?.({
                    ...(data?.data ?? {}),
                    [name]: editedValue
                });
            } else if (data?.code === CODE.ERROR_CODE_400 && data?.data?.[0]?.errorMessage) {
                // Có lỗi validation, hiển thị popup confirm
                setValidationMessage(data.data[0].errorMessage);
                setShowConfirmWithReason(false);
                setShowValidationConfirm(true);
            } else {
                toast.error(data?.message || "Cập nhật thất bại");
                setShowConfirmWithReason(false);
            }
        } catch (error: any) {
            console.error(error);
            setShowConfirmWithReason(false);
            const errorData = error?.response?.data;
            if (errorData?.code === CODE.ERROR_CODE_400 && errorData?.data?.[0]?.errorMessage) {
                // Có lỗi validation từ backend
                setValidationMessage(errorData.data[0].errorMessage);
                setShowValidationConfirm(true);
            } else {
                toast.error(errorData?.message || "Có lỗi xảy ra khi cập nhật");
            }
        }
    };

    const handleConfirmValidation = () => {
        setShowValidationConfirm(false);
        handleSaveSelect(undefined, true);
    };

    const handleCancelValidation = () => {
        setShowValidationConfirm(false);
    };

    const handleConfirmWithReason = () => {
        setShowConfirmWithReason(true);
    };

    const handleCancelWithReason = () => {
        setShowConfirmWithReason(false);
    };

    return {
        // States
        isEditingSelect,
        editedValue,
        showValidationConfirm,
        showConfirmWithReason,
        validationMessage,
        isLoadingSelectEditorInfo,

        // Setters
        setEditedValue,

        // Handlers
        handleEditSelect,
        handleCancelEdit,
        handleSaveSelect,
        handleConfirmValidation,
        handleCancelValidation,
        handleConfirmWithReason,
        handleCancelWithReason,
    };
};
